import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Alert,
  MenuItem,
  Paper,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import SendIcon from "@mui/icons-material/Send";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WaterIcon from "@mui/icons-material/Water";

const contactTypes = [
  "Patient/Caregiver",
  "Healthcare Provider",
  "Researcher",
  "Enterprise/Hospital",
  "Partnership",
  "Media",
  "Other",
];

const contactInfo = [
  {
    icon: <EmailIcon />,
    title: "Email",
    value: "reviewer@verainsight.com",
    color: "#1a237e",
  },
  {
    icon: <LanguageIcon />,
    title: "Website",
    value: "www.verainsight.com",
    color: "#00897b",
  },
  {
    icon: <LocationOnIcon />,
    title: "Location",
    value: "Minneapolis, MN",
    color: "#f9a825",
  },
];

const products = [
  {
    name: "AmyloidConnect",
    url: "www.amyloidconnect.com",
    color: "#667eea",
    status: "Live",
    icon: <FavoriteIcon fontSize="small" />,
  },
  {
    name: "NephroTrax",
    url: "www.nephrotrax.com",
    color: "#00897b",
    status: "Coming Soon",
    icon: <WaterIcon fontSize="small" />,
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    type: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields (Name, Email, Message)");
      return;
    }
    setSubmitted(true);
  };

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip
            label="Contact Us"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography variant="h2" fontWeight={800} gutterBottom>
            Get In Touch
          </Typography>
          <Typography
            variant="h5"
            sx={{ opacity: 0.9, fontWeight: 400, lineHeight: 1.6 }}
          >
            Whether you have questions, feedback, or partnership inquiries —
            we'd love to hear from you.
          </Typography>
        </Container>
      </Box>

      {/* CONTACT SECTION */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* LEFT - Contact Info */}
            <Grid item xs={12} md={4}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Contact Information
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.8, mb: 4 }}
              >
                Reach out to us for any questions about our platforms,
                partnerships, or enterprise solutions.
              </Typography>

              {/* Contact Details */}
              {contactInfo.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      color: item.color,
                      bgcolor: `${item.color}15`,
                      p: 1,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.title}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Products Box */}
              <Paper sx={{ mt: 2, p: 3, borderRadius: 3 }} elevation={1}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Our Products
                </Typography>
                {products.map((product) => (
                  <Box
                    key={product.name}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 2,
                      borderBottom: "1px solid #f0f0f0",
                      "&:last-child": { mb: 0, pb: 0, borderBottom: "none" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: product.color }}>{product.icon}</Box>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: product.color }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.url}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={product.status}
                      size="small"
                      color={product.status === "Live" ? "success" : "warning"}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* RIGHT - Contact Form */}
            <Grid item xs={12} md={8}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Send Us a Message
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Fill out the form below and we'll get back to you within 24
                    hours.
                  </Typography>

                  {submitted ? (
                    <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography fontWeight={600}>
                        Message Sent Successfully!
                      </Typography>
                      <Typography variant="body2">
                        Thank you for reaching out! We'll get back to you within
                        24 hours.
                      </Typography>
                    </Alert>
                  ) : (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full Name *"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address *"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Organization"
                          name="organization"
                          value={form.organization}
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="I am a..."
                          name="type"
                          value={form.type}
                          onChange={handleChange}
                          variant="outlined"
                        >
                          {contactTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={5}
                          label="Message *"
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help you..."
                          variant="outlined"
                        />
                      </Grid>

                      {error && (
                        <Grid item xs={12}>
                          <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {error}
                          </Alert>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<SendIcon />}
                          onClick={handleSubmit}
                          sx={{
                            background:
                              "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
                            fontWeight: 700,
                            py: 1.5,
                            borderRadius: 2,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
